import type { Attributes, Usertour } from "usertour.js";
import { CONFIGURACAO_USERTOUR } from "@/configs/system";

// --------------------------------- TYPES ---------------------------------
type SessaoUsertour = {
	readonly token: string;
	readonly identidade: string;
	readonly nome?: string;
	readonly aoNavegar: (url: string) => void;
};

// -------------------------------- HELPERS --------------------------------
let carregamento: Promise<Usertour> | null = null;
let sdkCarregado: Usertour | null = null;
let sessaoDesejada: SessaoUsertour | null = null;
let sessaoAplicada: SessaoUsertour | null = null;
let versaoSessao = 0;
let sincronizando = false;

function mesmaIdentificacao(atual: SessaoUsertour | null, proxima: SessaoUsertour | null): boolean {
	return (
		atual?.token === proxima?.token && atual?.identidade === proxima?.identidade && atual?.nome === proxima?.nome
	);
}

function limparIdentificacao(): void {
	sessaoAplicada = null;
	try {
		sdkCarregado?.reset();
	} catch {
		console.error("Não foi possível limpar a identificação do Usertour.");
	}
}

function navegar(url: string): void {
	sessaoDesejada?.aoNavegar(url);
}

async function carregarSdk(token: string): Promise<Usertour> {
	globalThis.window.USERTOURJS_ENV_VARS = CONFIGURACAO_USERTOUR;
	const { default: sdk } = await import("usertour.js");
	// Aguarda o loader explicitamente para tratar a falha antes de enfileirar chamadas.
	await sdk.load();
	sdk.init(token);
	sdk.setCustomNavigate(navegar);
	sdkCarregado = sdk;
	return sdk;
}

function obterSdk(token: string): Promise<Usertour> {
	carregamento ??= carregarSdk(token).catch((error: unknown) => {
		carregamento = null;
		throw error;
	});
	return carregamento;
}

async function aplicarIdentificacao(sessao: SessaoUsertour, versao: number): Promise<void> {
	const sdk = await obterSdk(sessao.token);
	if (versao !== versaoSessao) return;

	const atributos: Attributes = {};
	if (sessao.nome) atributos.name = sessao.nome;
	await sdk.identify(sessao.identidade, atributos);

	if (versao !== versaoSessao) {
		// O reset imediato do logout também precisa prevalecer sobre uma resposta tardia.
		limparIdentificacao();
		return;
	}
	sessaoAplicada = sessao;
}

async function processarSincronizacao(): Promise<void> {
	if (sincronizando) return;
	sincronizando = true;
	try {
		while (sessaoDesejada && !mesmaIdentificacao(sessaoAplicada, sessaoDesejada)) {
			const sessao = sessaoDesejada;
			const versao = versaoSessao;
			try {
				await aplicarIdentificacao(sessao, versao);
			} catch {
				limparIdentificacao();
				console.error("Não foi possível conectar o Usertour. A sessão da aplicação foi preservada.");
				// Só prossegue se uma nova sessão chegou durante a tentativa; não repete a falha em loop.
				if (versao === versaoSessao) break;
			}
		}
	} finally {
		sincronizando = false;
	}
}

// ------------------------------- FUNCTIONS -------------------------------
export function sincronizarSessaoUsertour(sessao: SessaoUsertour | null): void {
	if (mesmaIdentificacao(sessaoDesejada, sessao)) {
		sessaoDesejada = sessao;
		return;
	}

	const mudouIdentidade = sessaoDesejada?.identidade !== sessao?.identidade;
	sessaoDesejada = sessao;
	versaoSessao += 1;
	if (mudouIdentidade) limparIdentificacao();
	void processarSincronizacao();
}
